// ── Light/Dark theme — system preference by default, manual override wins.
//
// How it works: an explicit choice is stored in localStorage and applied as
// a `data-theme="light"|"dark"` attribute on <html>. With no explicit choice
// (fresh browser, or user never touched the toggle), no attribute is set at
// all and index.css's `@media (prefers-color-scheme: light)` block decides —
// so the app just follows the OS/browser setting until someone overrides it.
const STORAGE_KEY = 'uniedd-theme'

function systemPrefersLight() {
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ?? false
}

// Effective theme right now — explicit override if one was ever saved,
// otherwise whatever the OS/browser currently prefers.
export function getEffectiveTheme() {
  const saved = localStorage.getItem(STORAGE_KEY)
  if (saved === 'light' || saved === 'dark') return saved
  return systemPrefersLight() ? 'light' : 'dark'
}

function apply(theme) {
  document.documentElement.setAttribute('data-theme', theme)
}

// Run once, as early as possible (before first paint) — sets the attribute
// only if the user has an explicit saved choice. No attribute = pure CSS
// media-query behavior, kept in sync live if the OS theme changes.
export function initTheme() {
  const saved = localStorage.getItem(STORAGE_KEY)
  if (saved === 'light' || saved === 'dark') apply(saved)

  // If the user never manually overrides, keep following the OS live
  // (e.g. their system switches to dark mode at sunset).
  window.matchMedia?.('(prefers-color-scheme: light)').addEventListener?.('change', () => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      document.documentElement.removeAttribute('data-theme')
    }
  })
}

export function toggleTheme() {
  const next = getEffectiveTheme() === 'light' ? 'dark' : 'light'
  localStorage.setItem(STORAGE_KEY, next)
  apply(next)
  return next
}
